import { DataVoyagerWidgetData, WidgetType } from '@/models/PanelWidgets';
import {
  fetchDataVoyagerStateByWidgetId,
  fetchLatestDataVoyagerState,
} from '@/store/thunks/FetchDataVoyager';
import { openWidget, OpenWidgetPayload } from '@/store/reducers/UIState';
import { ThreadId } from '@/models/Thread';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DataVoyagerWidgetStoreState {
  stateForThreads: Record<ThreadId, DataVoyagerWidgetData>;
}

const initialState: DataVoyagerWidgetStoreState = {
  stateForThreads: {},
};

export interface CacheDataVoyagerPayload {
  threadId: ThreadId;
  dvData: DataVoyagerWidgetData;
}

/*************
 * reducers *
 *************/
export const cacheDataForThreadReducer = (
  state: DataVoyagerWidgetStoreState,
  action: PayloadAction<CacheDataVoyagerPayload>,
) => {
  const { threadId, dvData } = action.payload;
  state.stateForThreads[threadId] = dvData;
};

export const openDataVoyagerWidgetReducer = (
  state: DataVoyagerWidgetStoreState,
  action: PayloadAction<OpenWidgetPayload>,
) => {
  const { threadId, widgetData } = action.payload;
  if (!widgetData || widgetData.type !== WidgetType.DATA_VOYAGER) {
    return;
  }

  cacheDataForThreadReducer(state, {
    type: 'dataVoyagerWidgetStore/cacheDataForThread',
    payload: {
      threadId,
      dvData: widgetData,
    },
  });
};

/*************
 * selectors *
 *************/
export const getDataVoyagerStateForThreadSelector = (
  state: DataVoyagerWidgetStoreState,
  threadId: ThreadId | undefined,
): DataVoyagerWidgetData | null => {
  if (!threadId) {
    return null;
  }
  return state.stateForThreads[threadId];
};

/*************
 * slice *
 *************/
export const dataVoyagerWidgetStoreSlice = createSlice({
  name: 'dataVoyagerWidgetStore',
  initialState,
  reducers: {
    cacheDvDataForThread: cacheDataForThreadReducer,
  },
  extraReducers: (builder) => {
    builder
      .addCase(openWidget, openDataVoyagerWidgetReducer)
      .addCase(fetchLatestDataVoyagerState.fulfilled, cacheDataForThreadReducer)
      .addCase(
        fetchDataVoyagerStateByWidgetId.fulfilled,
        cacheDataForThreadReducer,
      );
  },
  selectors: {
    getDataVoyagerStateForThread: getDataVoyagerStateForThreadSelector,
  },
});

export const { getDataVoyagerStateForThread } =
  dataVoyagerWidgetStoreSlice.selectors;
export const { cacheDvDataForThread } = dataVoyagerWidgetStoreSlice.actions;
export const dataVoyagerWidgetStoreReducer =
  dataVoyagerWidgetStoreSlice.reducer;
